import PropTypes from 'prop-types';
import React from 'react';
import styled from 'styled-components';

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.9);
  display: ${(props) => (props.isOpen ? 'flex' : 'none')};
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  z-index: 9999;
  overflow-y: auto;
`;

const ModalContent = styled.div`
  position: relative;
  width: 100%;
  max-width: 900px;
  max-height: 90vh;
  background: ${(props) =>
    props.theme?.name === 'dark' ? '#1a1a2e' : '#ffffff'};
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  border: 1px solid
    ${(props) =>
      props.theme?.name === 'dark'
        ? 'rgba(255, 255, 255, 0.1)'
        : 'rgba(0, 0, 0, 0.1)'};
  display: flex;
  flex-direction: column;
`;

const CloseButton = styled.button`
  position: absolute;
  top: 16px;
  right: 16px;
  z-index: 10;
  width: 40px;
  height: 40px;
  background: rgba(0, 0, 0, 0.5);
  border: 2px solid #fa4616;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: rgba(255, 255, 255, 0.7);
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    color: white;
    background: rgba(0, 0, 0, 0.7);
    border-color: #e03e0c;
  }
`;

const VideoContainer = styled.div`
  position: relative;
  padding-bottom: 56.25%; /* 16:9 aspect ratio */
  height: 0;
  overflow: hidden;
  background: #000;
  flex-shrink: 0;

  iframe {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    border: none;
  }
`;

const ModalInfo = styled.div`
  padding: 24px;
  overflow-y: auto;
  flex: 1;
  min-height: 0;
`;

const BadgesRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 12px;
`;

const NewBadge = styled.span`
  padding: 4px 10px;
  background: #fa4616;
  color: white;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
`;

const DurationText = styled.span`
  display: flex;
  align-items: center;
  gap: 6px;
  color: ${(props) => (props.theme?.name === 'dark' ? '#94A3B8' : '#64748B')};
  font-size: 14px;
`;

const ModalTitle = styled.h2`
  font-size: 24px;
  font-weight: 700;
  color: ${(props) => (props.theme?.name === 'dark' ? '#ffffff' : '#0F172A')};
  margin: 0 0 12px 0;
`;

const ModalDescription = styled.p`
  font-size: 16px;
  color: ${(props) => (props.theme?.name === 'dark' ? '#94A3B8' : '#64748B')};
  margin: 0 0 16px 0;
  line-height: 1.6;
`;

const LearnMoreButton = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  background: ${(props) =>
    props.theme?.name === 'dark'
      ? 'rgba(255, 255, 255, 0.1)'
      : 'rgba(0, 0, 0, 0.05)'};
  border-radius: 8px;
  color: ${(props) => (props.theme?.name === 'dark' ? '#ffffff' : '#0F172A')};
  text-decoration: none;
  font-size: 14px;
  font-weight: 500;
  transition: all 0.2s;

  &:hover {
    background: ${(props) =>
      props.theme?.name === 'dark'
        ? 'rgba(255, 255, 255, 0.15)'
        : 'rgba(0, 0, 0, 0.08)'};
  }
`;

const XIcon = () => (
  <svg
    width="20"
    height="20"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M6 18L18 6M6 6l12 12"
    />
  </svg>
);

const ClockIcon = () => (
  <svg
    width="16"
    height="16"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
    />
  </svg>
);

const ArrowTopRightIcon = () => (
  <svg
    width="16"
    height="16"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
    />
  </svg>
);

const VideoModal = ({ video, onClose, mode, helpBaseUrl }) => {
  if (!video) return null;

  const vimeoEmbedUrl = `https://player.vimeo.com/video/${video.vimeoId}?autoplay=1&title=0&byline=0&portrait=0`;
  const learnMoreUrl = video.helpLink || helpBaseUrl;

  return (
    <ModalOverlay isOpen={!!video} onClick={onClose}>
      <ModalContent onClick={(e) => e.stopPropagation()}>
        <CloseButton onClick={onClose}>
          <XIcon />
        </CloseButton>
        <VideoContainer>
          <iframe
            src={vimeoEmbedUrl}
            frameBorder="0"
            allow="autoplay; fullscreen; picture-in-picture"
            allowFullScreen
            title={video.title}
          />
        </VideoContainer>
        <ModalInfo>
          <BadgesRow>
            {video.isNew && <NewBadge>NEW</NewBadge>}
            <DurationText>
              <ClockIcon /> {video.duration}
            </DurationText>
          </BadgesRow>
          <ModalTitle>{video.title}</ModalTitle>
          <ModalDescription>{video.description}</ModalDescription>
          {mode === 'howto' && learnMoreUrl && (
            <LearnMoreButton
              href={learnMoreUrl}
              target="_blank"
              rel="noopener noreferrer"
            >
              <span>Learn More</span>
              <ArrowTopRightIcon />
            </LearnMoreButton>
          )}
        </ModalInfo>
      </ModalContent>
    </ModalOverlay>
  );
};

VideoModal.propTypes = {
  video: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    title: PropTypes.string.isRequired,
    description: PropTypes.string.isRequired,
    duration: PropTypes.string.isRequired,
    vimeoId: PropTypes.string.isRequired,
    isNew: PropTypes.bool,
    helpLink: PropTypes.string,
  }),
  onClose: PropTypes.func.isRequired,
  mode: PropTypes.oneOf(['discover', 'howto']),
  helpBaseUrl: PropTypes.string,
};

export default VideoModal;
